/**
 * `SpeakerStack` — hand-painted stack lettering. `docs/design-direction.md` §8.6.
 *
 * Three `--bg-panel` cabinets with 1px keylines and a bass-bin cutout arc, with the
 * artist's or tag's name split **one word per panel, each word stretched to fill its
 * panel width**. That is not a typographic effect for its own sake: it is how real
 * sound-system stack lettering works, because the word has to fill the box, so the
 * letters distort to fit. `<text textLength="…" lengthAdjust="spacingAndGlyphs">` is
 * the only honest way to say that in a browser — a CSS `transform: scaleX()` would
 * distort the stroke weights of a whole line uniformly and a `font-stretch` would
 * pick a width axis the face may not have.
 *
 * It is the loudest typography in the app and the quietest colour: the flare behind
 * it is two overlapping conic gradients in `--accent-quiet` at 10%. That restraint is
 * what earns the ink on Now Playing (§8.6) — one loud object per screen, and here the
 * loud object is the lettering, not a colour.
 *
 * Word distribution: one panel per word up to `maxPanels`, after which the remaining
 * words share the last panel. A three-word name gets the canonical three cabinets; a
 * one-word name gets one cabinet rather than two empty boxes, because an empty
 * cabinet is a placeholder and this system omits rather than pads (§12.6).
 *
 * The stack is a `role="img"` with the full name: reading three separate stretched
 * words as three separate strings would spell the name out as fragments.
 */

import type { JSX } from 'react';

import styles from './components.module.css';
import { cx } from './primitives/Rule';

/** §8.6: three stacked cabinets. */
export const DEFAULT_PANELS = 3;

/** The lettering's user-space box. `textLength` is in these units, so it is numeric. */
const PANEL_W = 100;
const PANEL_H = 26;
/** The keyline inset, so a stretched glyph never touches the cabinet edge. */
const PANEL_PAD = 4;
const TEXT_LENGTH = PANEL_W - PANEL_PAD * 2;

/** Split a name into at most `maxPanels` groups, one word each until the last. */
export function splitWords(name: string, maxPanels: number): string[] {
  const words = name
    .trim()
    .split(/\s+/u)
    .filter((word) => word !== '');
  if (words.length === 0) return [];
  if (words.length <= maxPanels) return words;
  const head = words.slice(0, maxPanels - 1);
  return [...head, words.slice(maxPanels - 1).join(' ')];
}

export interface SpeakerStackProps {
  /** The artist or tag name. Printed as-is; the panels do the shouting, not the copy. */
  readonly name: string;
  readonly maxPanels?: number;
  readonly className?: string | undefined;
}

export function SpeakerStack({
  name,
  maxPanels = DEFAULT_PANELS,
  className,
}: SpeakerStackProps): JSX.Element {
  const words = splitWords(name, Math.max(1, maxPanels));

  return (
    <div className={cx(styles.stack, className)} role="img" aria-label={name}>
      {words.map((word, index) => (
        <span className={styles.panel} key={`${String(index)}:${word}`}>
          <svg
            className={cx('t-display', styles.word)}
            viewBox={`0 0 ${String(PANEL_W)} ${String(PANEL_H)}`}
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
          >
            <text
              x={PANEL_PAD}
              y={PANEL_H - PANEL_PAD * 2}
              textLength={TEXT_LENGTH}
              lengthAdjust="spacingAndGlyphs"
              fontSize={PANEL_H - PANEL_PAD * 2}
            >
              {word}
            </text>
          </svg>
        </span>
      ))}
    </div>
  );
}
